import React, { useState, useEffect } from 'react';
import { productsAPI } from '../services/api';
import './ImageSelector.css';

function ImageSelector({ value, onChange }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBrowser, setShowBrowser] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFolder, setUploadFolder] = useState('');

  useEffect(() => {
    if (showBrowser) {
      loadImages();
    }
  }, [showBrowser]);

  const loadImages = async () => {
    try {
      const response = await productsAPI.listImages();
      setImages(response.data.images);
    } catch (error) {
      console.error('Error loading images:', error);
      alert('Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectImage = (imagePath) => {
    onChange(imagePath);
    setShowBrowser(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    if (uploadFolder) {
      formData.append('subfolder', uploadFolder);
    }

    setUploading(true);
    try {
      const response = await productsAPI.uploadImage(formData);
      onChange(response.data.path);
      setShowBrowser(false);
      alert('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image: ' + (error.response?.data?.error || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    // Check if it's already a full URL (S3)
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    // Local development
    return `http://localhost:5001/api/products/images/${imagePath}`;
  };

  // Group images by folder
  const groupedImages = images.reduce((acc, img) => {
    const folder = img.folder || 'root';
    if (!acc[folder]) acc[folder] = [];
    acc[folder].push(img);
    return acc;
  }, {});

  return (
    <div className="image-selector">
      <label>Product Image:</label>

      <div className="selected-image-preview">
        {value ? (
          <div className="image-preview">
            <img src={getImageUrl(value)} alt="Selected product" />
            <p className="image-path">{value}</p>
          </div>
        ) : (
          <p className="no-image">No image selected</p>
        )}
      </div>

      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => setShowBrowser(!showBrowser)}
      >
        {value ? 'Change Image' : 'Select Image'}
      </button>

      {value && (
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => onChange('')}
          style={{ marginLeft: '10px' }}
        >
          Remove Image
        </button>
      )}

      {showBrowser && (
        <div className="image-browser-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Select Product Image</h3>
              <button onClick={() => setShowBrowser(false)} className="close-btn">×</button>
            </div>

            <div className="modal-body">
              {/* Upload Section */}
              <div className="upload-section">
                <h4>Upload New Image</h4>
                <div className="upload-form">
                  <input
                    type="text"
                    placeholder="Subfolder (optional)"
                    value={uploadFolder}
                    onChange={(e) => setUploadFolder(e.target.value)}
                    className="form-input"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="form-input"
                  />
                  {uploading && <p>Uploading...</p>}
                </div>
              </div>

              {/* Browse Section */}
              <div className="browse-section">
                <h4>Browse Existing Images</h4>
                {loading ? (
                  <p>Loading images...</p>
                ) : (
                  <div className="image-folders">
                    {Object.keys(groupedImages).map((folder) => (
                      <div key={folder} className="image-folder">
                        <h5>{folder}</h5>
                        <div className="image-grid">
                          {groupedImages[folder].map((img) => (
                            <div
                              key={img.path}
                              className={`image-item ${value === img.path ? 'selected' : ''}`}
                              onClick={() => handleSelectImage(img.path)}
                            >
                              <img src={getImageUrl(img.path)} alt={img.filename} />
                              <p className="image-filename">{img.filename}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageSelector;
